import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { supabase } from '@/lib/supabase';

// Stripe loader utility - ensures singleton instance
let stripePromise: Promise<Stripe | null>;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    
    if (!publishableKey) {
      console.error('Missing VITE_STRIPE_PUBLISHABLE_KEY environment variable');
      return Promise.resolve(null);
    }
    
    stripePromise = loadStripe(publishableKey);
  }
  
  return stripePromise;
};

// Price IDs for our subscription plans
export const STRIPE_PRICE_IDS = {
  MONTHLY: import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_monthly_placeholder',
  YEARLY: import.meta.env.VITE_STRIPE_PRICE_YEARLY || 'price_yearly_placeholder',
} as const;

// Create checkout session via Supabase Edge Function
interface CheckoutSessionOptions {
  priceId: string;
  planId: 'monthly' | 'yearly';
  successUrl?: string;
  cancelUrl?: string;
}

const resolveUrl = (url: string | undefined, fallbackPath: string) => {
  if (url && /^https?:\/\//.test(url)) {
    return url;
  }
  const origin = (
    import.meta.env.VITE_APP_URL ??
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173')
  ).replace(/\/$/, '');
  const path = url ?? fallbackPath;
  return path.startsWith('http')
    ? path
    : `${origin}${path.startsWith('/') ? path : `/${path}`}`;
};

export const createCheckoutSession = async ({
  priceId,
  planId,
  successUrl,
  cancelUrl,
}: CheckoutSessionOptions): Promise<{ url: string }> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Get the current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('Session error:', sessionError);
    throw new Error(`Session error: ${sessionError.message}`);
  }

  if (!session) {
    console.error('No active session found');
    throw new Error('No active session. Please sign in to continue.');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      priceId,
      planId,
      successUrl: resolveUrl(successUrl ?? import.meta.env.VITE_STRIPE_SUCCESS_URL, '/upgrade/success'),
      cancelUrl: resolveUrl(cancelUrl ?? import.meta.env.VITE_STRIPE_CANCEL_URL, '/upgrade/cancel'),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Error response:', error);
    throw new Error(error.message || `Failed to create checkout session (${response.status})`);
  }

  const result = await response.json();
  return result;
};

// Create customer portal session for managing billing and invoices
export const createCustomerPortalSession = async (): Promise<{ url: string }> => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Get the current session
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  if (sessionError) {
    console.error('Session error:', sessionError);
    throw new Error(`Session error: ${sessionError.message}`);
  }

  if (!session) {
    console.error('No active session found');
    throw new Error('No active session. Please sign in to continue.');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/create-portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      returnUrl: `${window.location.origin}/app/profile`,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Error response:', error);
    throw new Error(error.message || `Failed to create portal session (${response.status})`);
  }

  const result = await response.json();
  return result;
};
